import React, { useEffect, useRef, useState } from 'react';
import { API_ENDPOINTS, apiClient } from '../../config/api';
import { auth } from '../../firebase/config';
import AskRiaInputBar from './AskRiaInputBar';
import RiaNoteLinks, { extractRiaNoteLinks } from '../notes/RiaNoteLinks';
import CmaReportLink, { extractCmaReportLink } from './CmaReportLink';
import riaLogo from '../../assets/center-logo.png';
import { getViewerTimezone } from '../../utils/dateTime';
import { formatRiaMessage } from '../../utils/formatRiaMessage';
import './FloatingRiaWidget.css';

const BTN_SIZE = 56;
const BTN_MARGIN = 24;
const POSITION_STORAGE_KEY = 'ria_fab_position';
// Distinguishes a drag from a click — a mousedown/up pair that never moves
// more than this many pixels is treated as a click that opens the panel.
const DRAG_THRESHOLD = 6;
// How close to the top (in px) of the panel body triggers the next older-history
// fetch — both the IntersectionObserver's rootMargin and the post-restore
// chain-check below use this same value, so they agree on "near the top".
const RIA_HISTORY_LOOKAHEAD_PX = 60;

// Suggested prompts shown only before the first message (mirrors the
// reference design's "chip-row" on first load).
const SUGGESTION_CHIPS = [
  "What's due this week?",
  'Show my active transactions',
  'Any overdue items?',
];

// Helper: get Firebase token — same wait-for-auth-state pattern used on
// DashboardPage / ConversationPage.
const getFirebaseToken = () => {
  return new Promise((resolve, reject) => {
    if (auth.currentUser) {
      auth.currentUser.getIdToken(true).then(resolve).catch(reject);
      return;
    }
    const timeoutId = setTimeout(() => {
      unsubscribe();
      reject(new Error('Firebase auth timeout'));
    }, 10000);
    const unsubscribe = auth.onAuthStateChanged(async (user) => {
      clearTimeout(timeoutId);
      unsubscribe();
      if (user) {
        try {
          const token = await user.getIdToken(true);
          resolve(token);
        } catch (error) {
          reject(error);
        }
      } else {
        reject(new Error('No authenticated user'));
      }
    });
  });
};

// Defensive: the /ria/chat response has never been observed to include a
// "which transactions this came from" field anywhere else in this codebase.
// Only render a context tag if the API actually returns one of these — never
// fabricate one.
const extractContextTag = (data) => {
  const raw = data?.context_label ?? data?.contextLabel ?? data?.context ?? data?.sources ?? data?.transactions;
  if (!raw) return null;
  if (Array.isArray(raw)) {
    if (raw.length === 0) return null;
    const names = raw.map((t) => (typeof t === 'string' ? t : t?.name || t?.transactionName)).filter(Boolean);
    return names.length ? `Across ${names.length} transaction${names.length !== 1 ? 's' : ''}` : null;
  }
  return typeof raw === 'string' ? raw : null;
};

const RIA_MONTH_ABBREV = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

// Per-message "date · time" label — mirrors the mobile app's RiaChatScreen.formatTime
// (abbreviated month/day, year only when it differs from the current one) so a
// message's timestamp is always visible without needing to scroll to trigger the
// day-group pill above.
const formatRiaMessageTimestamp = (date) => {
  const zone = getViewerTimezone();
  const time = date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true, timeZone: zone });
  const parts = {};
  new Intl.DateTimeFormat('en-US', { year: 'numeric', month: 'numeric', day: 'numeric', timeZone: zone })
    .formatToParts(date).forEach((p) => { if (p.type !== 'literal') parts[p.type] = p.value; });
  const nowYear = new Intl.DateTimeFormat('en-US', { year: 'numeric', timeZone: zone }).format(new Date());
  const sameYear = parts.year === nowYear;
  const monthIdx = Number(parts.month) - 1;
  const datePart = sameYear
    ? `${RIA_MONTH_ABBREV[monthIdx]} ${parts.day}`
    : `${RIA_MONTH_ABBREV[monthIdx]} ${parts.day}, ${parts.year}`;
  return `${datePart} · ${time}`;
};

// Same localStorage 'user' cache DashboardPage/Header/etc. all read from — no
// extra API call needed just to label a chat bubble with a name.
const getUserDisplayName = () => {
  try {
    const u = JSON.parse(localStorage.getItem('user') || '{}');
    const name = [u.firstName, u.lastName].filter(Boolean).join(' ');
    return name || u.email || 'You';
  } catch {
    return 'You';
  }
};

// Teams/WhatsApp-style date label: "Today", "Yesterday", or "Month D, YYYY".
const riaDateLabel = (date) => {
  const zone = getViewerTimezone();
  const dayKey = (d) => d.toLocaleDateString('en-CA', { timeZone: zone });
  const todayKey = dayKey(new Date());
  const targetKey = dayKey(date);
  if (targetKey === todayKey) return 'Today';
  if (dayKey(new Date(Date.now() - 86400000)) === targetKey) return 'Yesterday';
  return date.toLocaleDateString([], { month: 'long', day: 'numeric', year: 'numeric', timeZone: zone });
};

// Groups a flat, chronologically-ordered message list into day buckets so the
// UI can render one sticky date header per day (position:sticky handles the
// "floating overlay while scrolling" behavior with no manual scroll math).
const groupRiaMessagesByDay = (messages) => {
  const groups = [];
  let current = null;
  messages.forEach((msg) => {
    const d = msg.createdAt instanceof Date && !isNaN(msg.createdAt) ? msg.createdAt : null;
    const dateKey = d ? d.toLocaleDateString('en-CA', { timeZone: getViewerTimezone() }) : 'unknown';
    if (!current || current.dateKey !== dateKey) {
      current = { dateKey, label: d ? riaDateLabel(d) : '', items: [] };
      groups.push(current);
    }
    current.items.push(msg);
  });
  return groups;
};

// Normalizes a backend conversation's messages into the shape the widget renders,
// keeping a real Date (createdAt) alongside the pre-formatted time string so
// messages can be grouped into day buckets for the sticky date headers.
const normalizeRiaMessages = (conv) =>
  (conv.messages || []).map((msg) => ({
    role: msg.role === 'user' ? 'user' : 'ria',
    content: msg.content,
    createdAt: msg.created_at ? new Date(msg.created_at) : null,
    time: msg.created_at ? formatRiaMessageTimestamp(new Date(msg.created_at)) : '',
    noteLinks: msg.role === 'user' ? undefined : extractRiaNoteLinks(msg),
    cmaReportLink: msg.role === 'user' ? undefined : extractCmaReportLink(msg),
  }));

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

// Default bottom-right resting spot, clamped to the current viewport.
const getDefaultPosition = () => ({
  x: clamp(window.innerWidth - BTN_SIZE - BTN_MARGIN, 0, window.innerWidth - BTN_SIZE),
  y: clamp(window.innerHeight - BTN_SIZE - BTN_MARGIN, 0, window.innerHeight - BTN_SIZE),
});

const loadStoredPosition = () => {
  try {
    const raw = window.sessionStorage.getItem(POSITION_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (typeof parsed?.x !== 'number' || typeof parsed?.y !== 'number') return null;
    // Re-clamp in case the viewport changed (e.g. rotated device) since it was saved.
    return {
      x: clamp(parsed.x, 0, window.innerWidth - BTN_SIZE),
      y: clamp(parsed.y, 0, window.innerHeight - BTN_SIZE),
    };
  } catch {
    return null;
  }
};

// Site-wide floating "Ask Ria" bubble — same Ria chat API DashboardPage uses for
// history (GET /ria/conversations + /ria/conversations/:id) and for sending
// messages (POST /ria/chat). Rendered once at the App level on every
// authenticated page except Dashboard (which already has its own inline
// Ask Ria card), so it's a self-contained widget with its own conversation
// state — it does not share state with the Dashboard chat card.
const FloatingRiaWidget = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [loading, setLoading] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [hasLoadedHistory, setHasLoadedHistory] = useState(false);
  const [position, setPosition] = useState(() => loadStoredPosition() || getDefaultPosition());
  const [isDragging, setIsDragging] = useState(false);
  const conversationIdRef = useRef(null);
  const bodyRef = useRef(null);
  const panelRef = useRef(null);
  const btnRef = useRef(null);
  const dragStateRef = useRef(null); // { startX, startY, originX, originY, moved }
  // Older-history pagination (scroll-to-top loads the previous conversation, Teams-style)
  const riaSeenConvIdsRef = useRef(new Set());
  const riaOldestDateRef = useRef(null); // 'YYYY-MM-DD' cursor for the next end_date fetch
  const riaPendingQueueRef = useRef([]); // fetched-but-not-yet-displayed conversation summaries
  const riaHasMoreHistoryRef = useRef(true);
  const riaTopSentinelRef = useRef(null);
  // Background prefetch: once a conversation is queued, fetch its full message detail
  // ahead of time so it's just an insert (no network wait) when the user actually
  // scrolls to it — display timing is unchanged, still gated by the sentinel below.
  const riaDetailCacheRef = useRef(new Map()); // conversation id -> full conversation detail
  const riaPrefetchingIdRef = useRef(null); // id currently being prefetched, to avoid duplicate fetches
  const [loadingOlder, setLoadingOlder] = useState(false);
  const [reachedHistoryStart, setReachedHistoryStart] = useState(false);
  // Date pill only shows while actively scrolling (Google/Apple Photos pattern) —
  // fades in on scroll, fades back out ~1s after scrolling stops, so it never
  // sits persistently over static content.
  const [datePillVisible, setDatePillVisible] = useState(false);
  const scrollStopTimeoutRef = useRef(null);
  // Exactly one date pill is ever shown (Teams/WhatsApp), computed from whichever
  // day-group is currently at the top of the viewport — NOT one sticky header per
  // day-group, which lets two pills appear stuck/visible at once when a day-group
  // is short enough that the next one is already on-screen too.
  const [activeDateLabel, setActiveDateLabel] = useState('');
  const riaGroupsRef = useRef([]); // latest groupRiaMessagesByDay(messages) result, set at render time
  const riaDayGroupNodesRef = useRef(new Map()); // dateKey -> DOM node, for scroll-position lookups
  const updateActiveDateLabel = () => {
    const bodyEl = bodyRef.current;
    const groups = riaGroupsRef.current;
    if (!bodyEl || groups.length === 0) return;
    const containerTop = bodyEl.getBoundingClientRect().top;
    let active = groups[0];
    for (const group of groups) {
      const node = riaDayGroupNodesRef.current.get(group.dateKey);
      if (!node) continue;
      const nodeTop = node.getBoundingClientRect().top - containerTop + bodyEl.scrollTop;
      if (nodeTop <= bodyEl.scrollTop + 8) {
        active = group;
      } else {
        break;
      }
    }
    setActiveDateLabel(active.label);
  };
  const handlePanelScroll = () => {
    setDatePillVisible(true);
    updateActiveDateLabel();
    if (scrollStopTimeoutRef.current) clearTimeout(scrollStopTimeoutRef.current);
    scrollStopTimeoutRef.current = setTimeout(() => setDatePillVisible(false), 1000);
  };
  useEffect(() => () => {
    if (scrollStopTimeoutRef.current) clearTimeout(scrollStopTimeoutRef.current);
  }, []);
  // Mirrors `position` so the drag-end handler always reads the live value —
  // it's registered once per drag session, so a stale closure over `position`
  // state would otherwise persist the position from when dragging started.
  const positionRef = useRef(position);

  // Close on outside click — must also exclude the toggle button itself,
  // otherwise mousedown closes the panel and the immediately-following click
  // on that same button re-opens it.
  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (e) => {
      if (
        panelRef.current && !panelRef.current.contains(e.target) &&
        btnRef.current && !btnRef.current.contains(e.target)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  // Auto-scroll to newest message — but ONLY for genuine "new message" events
  // (initial load, send, reply). shouldScrollToBottomRef is set to true right
  // before a setMessages call that should snap to the bottom; left untouched
  // (false) for history prepends, whose own scroll-position restore logic
  // (in loadOlderRiaConversation) must not be overridden here.
  const shouldScrollToBottomRef = useRef(false);
  useEffect(() => {
    if (shouldScrollToBottomRef.current && bodyRef.current) {
      bodyRef.current.scrollTop = bodyRef.current.scrollHeight;
      shouldScrollToBottomRef.current = false;
    }
  }, [messages, loading]);

  // ── Drag handling (mouse + touch) — bubble is draggable anywhere on the
  // viewport, and stays wherever it's dropped for the rest of the session. ──
  const persistPosition = (pos) => {
    try {
      window.sessionStorage.setItem(POSITION_STORAGE_KEY, JSON.stringify(pos));
    } catch {
      // sessionStorage unavailable (private browsing etc.) — position just won't persist
    }
  };

  const beginDrag = (clientX, clientY) => {
    dragStateRef.current = {
      startX: clientX,
      startY: clientY,
      originX: position.x,
      originY: position.y,
      moved: false,
    };
    setIsDragging(true);
  };

  const updateDrag = (clientX, clientY) => {
    const drag = dragStateRef.current;
    if (!drag) return;
    const dx = clientX - drag.startX;
    const dy = clientY - drag.startY;
    if (!drag.moved && Math.hypot(dx, dy) > DRAG_THRESHOLD) {
      drag.moved = true;
    }
    if (drag.moved) {
      const next = {
        x: clamp(drag.originX + dx, 0, window.innerWidth - BTN_SIZE),
        y: clamp(drag.originY + dy, 0, window.innerHeight - BTN_SIZE),
      };
      positionRef.current = next;
      setPosition(next);
    }
  };

  const endDrag = () => {
    const drag = dragStateRef.current;
    setIsDragging(false);
    if (drag?.moved) {
      persistPosition(positionRef.current);
    } else {
      // Not moved beyond the threshold — treat as a click.
      handleToggle();
    }
    dragStateRef.current = null;
  };

  useEffect(() => {
    if (!isDragging) return;
    const onMouseMove = (e) => updateDrag(e.clientX, e.clientY);
    const onMouseUp = () => endDrag();
    const onTouchMove = (e) => {
      const touch = e.touches[0];
      if (touch) updateDrag(touch.clientX, touch.clientY);
    };
    const onTouchEnd = () => endDrag();

    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
    document.addEventListener('touchmove', onTouchMove, { passive: true });
    document.addEventListener('touchend', onTouchEnd);
    return () => {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
      document.removeEventListener('touchmove', onTouchMove);
      document.removeEventListener('touchend', onTouchEnd);
    };
  }, [isDragging]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleBtnMouseDown = (e) => {
    e.preventDefault();
    beginDrag(e.clientX, e.clientY);
  };
  const handleBtnTouchStart = (e) => {
    const touch = e.touches[0];
    if (touch) beginDrag(touch.clientX, touch.clientY);
  };

  // Load most recent Ria conversation — same two-step call DashboardPage makes
  // (GET /ria/conversations?limit=1&duration=all, then GET /ria/conversations/:id).
  // duration=all: fetch the true latest conversation regardless of age,
  // not just one from the last 7 days (default filter is date-scoped).
  const loadHistory = async () => {
    setHistoryLoading(true);
    try {
      const firebaseToken = await getFirebaseToken();

      const listResp = await apiClient.get(API_ENDPOINTS.riaConversations, {
        headers: { 'Authorization': `Bearer ${firebaseToken}` },
        params: { limit: 1, duration: 'all' },
      });
      const conversations = listResp.data?.conversations || [];
      if (conversations.length === 0) return;

      const latestConv = conversations[0];
      const detailResp = await apiClient.get(API_ENDPOINTS.riaConversationById(latestConv.id), {
        headers: { 'Authorization': `Bearer ${firebaseToken}` },
      });
      const conv = detailResp.data?.conversation;
      if (!conv) return;

      const normalized = normalizeRiaMessages(conv);

      shouldScrollToBottomRef.current = true;
      setMessages((prev) => (prev.length === 0 ? normalized : prev));
      conversationIdRef.current = conversationIdRef.current || conv.id;

      // Seed the older-history cursor so scrolling up fetches the NEXT
      // conversation back, not this same one again.
      riaSeenConvIdsRef.current.add(latestConv.id);
      if (latestConv.updated_at) {
        riaOldestDateRef.current = latestConv.updated_at.slice(0, 10);
      }
    } catch (error) {
      console.warn('[FloatingRiaWidget] Failed to load Ria conversation history:', error?.message);
    } finally {
      setHistoryLoading(false);
    }
  };

  // Fetches the next batch of older conversation summaries using the end_date cursor.
  // end_date is date-granular (not a timestamp), so a batch can re-include conversations
  // already seen on the cursor's boundary day — filter those out, and if an entire batch
  // turns out to be duplicates, step the cursor one day earlier and retry.
  const fetchNextRiaConversationBatch = async (firebaseToken) => {
    if (!riaHasMoreHistoryRef.current) return [];
    let cursorDate = riaOldestDateRef.current;
    for (let attempt = 0; attempt < 30; attempt++) {
      const params = { limit: 20 };
      if (cursorDate) params.end_date = cursorDate;
      const resp = await apiClient.get(API_ENDPOINTS.riaConversations, {
        headers: { Authorization: `Bearer ${firebaseToken}` },
        params,
      });
      const batch = resp.data?.conversations || [];
      if (batch.length === 0) {
        riaHasMoreHistoryRef.current = false;
        return [];
      }
      const fresh = batch.filter((c) => !riaSeenConvIdsRef.current.has(c.id));
      const oldestInBatch = batch[batch.length - 1]; // ordered desc by updated_at
      const oldestDateStr = oldestInBatch?.updated_at?.slice(0, 10);
      if (fresh.length > 0) {
        fresh.forEach((c) => riaSeenConvIdsRef.current.add(c.id));
        if (oldestDateStr) riaOldestDateRef.current = oldestDateStr;
        return fresh;
      }
      if (!oldestDateStr) {
        riaHasMoreHistoryRef.current = false;
        return [];
      }
      const prevDay = new Date(`${oldestDateStr}T00:00:00Z`);
      prevDay.setUTCDate(prevDay.getUTCDate() - 1);
      cursorDate = prevDay.toISOString().slice(0, 10);
      riaOldestDateRef.current = cursorDate;
    }
    return [];
  };

  // Fetches one queued conversation's full message detail in the background and
  // caches it, so the reveal step below can skip the network wait entirely when
  // the user actually scrolls to it. Fire-and-forget: failures just mean the
  // reveal step falls back to fetching it live, same as before this existed.
  const prefetchNextQueuedRiaConversation = async (firebaseToken) => {
    const upcoming = riaPendingQueueRef.current[0];
    if (!upcoming) return;
    if (riaDetailCacheRef.current.has(upcoming.id)) return;
    if (riaPrefetchingIdRef.current === upcoming.id) return;
    riaPrefetchingIdRef.current = upcoming.id;
    try {
      const detailResp = await apiClient.get(API_ENDPOINTS.riaConversationById(upcoming.id), {
        headers: { Authorization: `Bearer ${firebaseToken}` },
      });
      const conv = detailResp.data?.conversation;
      if (conv) riaDetailCacheRef.current.set(upcoming.id, conv);
    } catch (error) {
      console.warn('[FloatingRiaWidget] Background prefetch failed (will fetch live on reveal):', error?.message);
    } finally {
      if (riaPrefetchingIdRef.current === upcoming.id) riaPrefetchingIdRef.current = null;
    }
  };

  // Loads the next older conversation (from the pending queue, fetching a fresh batch
  // from the server when the queue runs dry) and prepends its messages, preserving
  // scroll position so the view doesn't jump — the scroll-up-to-load-more mechanic.
  const loadingOlderRef = useRef(false); // synchronous re-entrancy lock — loadingOlder
  // (React state) only updates on the next render, so a second scroll/observer callback
  // firing in the same tick would otherwise see the stale "false" and race past the guard.
  const loadOlderRiaConversation = async () => {
    if (loadingOlderRef.current || reachedHistoryStart) return;
    loadingOlderRef.current = true;
    setLoadingOlder(true);
    // Defensively clear the scroll-to-bottom flag: it's only ever set by the
    // send/receive/initial-load paths, but if one of those is still mid-flight
    // when this prepend's setMessages also lands, both share the same
    // [messages] effect below — and it would jump back to the bottom right
    // after the prepend restores position.
    shouldScrollToBottomRef.current = false;
    try {
      const firebaseToken = await getFirebaseToken();
      if (riaPendingQueueRef.current.length === 0) {
        const fresh = await fetchNextRiaConversationBatch(firebaseToken);
        riaPendingQueueRef.current.push(...fresh);
      }
      if (riaPendingQueueRef.current.length === 0) {
        setReachedHistoryStart(true);
        return;
      }
      const next = riaPendingQueueRef.current.shift();
      // Reveal timing is unchanged — still only happens here, on the scroll trigger.
      // Only the data-fetch is decoupled: use the cached detail if the background
      // prefetch already finished it, otherwise fetch live (first scroll before any
      // prefetch has had time to complete).
      const cached = riaDetailCacheRef.current.get(next.id);
      let conv = cached;
      if (cached) {
        riaDetailCacheRef.current.delete(next.id);
      } else {
        const detailResp = await apiClient.get(API_ENDPOINTS.riaConversationById(next.id), {
          headers: { Authorization: `Bearer ${firebaseToken}` },
        });
        conv = detailResp.data?.conversation;
      }
      if (!conv) return;
      const normalized = normalizeRiaMessages(conv);

      const bodyEl = bodyRef.current;
      const prevScrollHeight = bodyEl ? bodyEl.scrollHeight : 0;
      setMessages((prev) => [...normalized, ...prev]);
      requestAnimationFrame(() => {
        if (!bodyEl) return;
        bodyEl.scrollTop = bodyEl.scrollHeight - prevScrollHeight;
        // The IntersectionObserver below only fires on a transition into the lookahead
        // zone, not just for sitting inside it — so if the conversation we just loaded
        // was shorter than that zone, the restored position never actually leaves it,
        // and the observer would otherwise never re-fire on its own even though more
        // history exists. Explicitly continue the chain in that case.
        if (bodyEl.scrollTop <= RIA_HISTORY_LOOKAHEAD_PX) {
          loadOlderRiaConversation();
        }
      });
      // Kick off background prefetch for the new front of the queue (fire-and-forget)
      // so the *next* scroll-triggered reveal is instant too.
      prefetchNextQueuedRiaConversation(firebaseToken);
    } catch (error) {
      console.warn('[FloatingRiaWidget] Failed to load older conversation:', error?.message);
    } finally {
      loadingOlderRef.current = false;
      setLoadingOlder(false);
    }
  };

  // Keep the latest loadOlderRiaConversation in a ref so the IntersectionObserver
  // effect below can stay mounted once without re-subscribing on every state change.
  const loadOlderRiaConversationRef = useRef(loadOlderRiaConversation);
  loadOlderRiaConversationRef.current = loadOlderRiaConversation;

  // Scroll-to-top trigger: an IntersectionObserver on a sentinel at the top of the
  // panel body, rather than a scrollTop/scrollHeight check — the latter never fires
  // when the current content is too short to overflow the container. rootMargin
  // starts the fetch ~60px before the sentinel reaches the true top edge, so history
  // is already in place by the time the user gets there. Kept small (not the mobile
  // app's 100-200px) because the panel body is only a few hundred px tall — a bigger
  // margin would fire while most of the panel is still unscrolled.
  useEffect(() => {
    if (!isOpen) return undefined;
    const sentinel = riaTopSentinelRef.current;
    const root = bodyRef.current;
    if (!sentinel || !root) return undefined;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          loadOlderRiaConversationRef.current();
        }
      },
      { root, threshold: 0, rootMargin: `${RIA_HISTORY_LOOKAHEAD_PX}px 0px 0px 0px` }
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [isOpen]);

  const handleToggle = () => {
    setIsOpen((prev) => {
      const next = !prev;
      if (next && !hasLoadedHistory) {
        setHasLoadedHistory(true);
        loadHistory();
      }
      return next;
    });
  };

  // Send message via the same POST /ria/chat endpoint DashboardPage.handleAskRia uses
  const sendMessage = async (message) => {
    if (!message || loading) return;

    shouldScrollToBottomRef.current = true;
    const sentAt = new Date();
    setMessages((prev) => [...prev, { role: 'user', content: message, createdAt: sentAt, time: formatRiaMessageTimestamp(sentAt) }]);
    setInputValue('');
    setLoading(true);

    try {
      const firebaseToken = await getFirebaseToken();
      const requestBody = {
        message,
        conversation_id: conversationIdRef.current || undefined,
        context: {},
      };

      const response = await apiClient.post(API_ENDPOINTS.riaChat, requestBody, {
        headers: {
          'Authorization': `Bearer ${firebaseToken}`,
          'Content-Type': 'application/json',
        },
        // Ria answers with tool calls take ~40s against the local backend
        // (fast on GKE); the 30s apiClient default aborts before they finish.
        // Raised past that for get_cma_report specifically — a real CMA
        // generation was observed taking 88s in stg, budgeted to 180s at the
        // gateway (rialty-api-gateway's analysisServiceCircuitBreaker).
        timeout: 200000,
      });

      if (response.data?.conversation_id) {
        conversationIdRef.current = response.data.conversation_id;
      }

      const riaReply = response.data?.message || response.data?.response || response.data?.content || response.data?.answer || 'No response from Ria.';
      const contextTag = extractContextTag(response.data);
      const replyAt = new Date();
      shouldScrollToBottomRef.current = true;
      const noteLinks = extractRiaNoteLinks(response.data);
      const cmaReportLink = extractCmaReportLink(response.data);
      setMessages((prev) => [...prev, { role: 'ria', content: riaReply, createdAt: replyAt, time: formatRiaMessageTimestamp(replyAt), contextTag, noteLinks, cmaReportLink }]);
    } catch (error) {
      console.error('[FloatingRiaWidget] Error asking Ria:', error);
      const errorAt = new Date();
      shouldScrollToBottomRef.current = true;
      setMessages((prev) => [...prev, { role: 'ria', content: "Sorry, I couldn't process your request. Please try again.", createdAt: errorAt, time: formatRiaMessageTimestamp(errorAt) }]);
    } finally {
      setLoading(false);
    }
  };

  const handleSend = () => sendMessage(inputValue.trim());
  const handleChipClick = (chip) => sendMessage(chip);

  // Computed once per render, reused by the scroll handler's active-date lookup.
  const riaMessageGroups = groupRiaMessagesByDay(messages);
  riaGroupsRef.current = riaMessageGroups;
  const userDisplayName = getUserDisplayName();

  return (
    <>
      {isOpen && (
        <div className="ria-fab-panel" ref={panelRef}>
          <div className="ria-fab-header">
            <div className="ria-fab-header-title">Ask Ria</div>
            <button className="ria-fab-close-btn" onClick={() => setIsOpen(false)} title="Close" aria-label="Close">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </div>

          <div className="ria-fab-body" ref={bodyRef} onScroll={handlePanelScroll}>
            {historyLoading ? (
              <div className="ria-fab-empty">Loading your last conversation…</div>
            ) : messages.length === 0 ? (
              <>
                <div className="ria-fab-empty">
                  Hi! I'm Ria, your real estate assistant. Ask me anything about your transactions.
                </div>
                <div className="ria-fab-chip-row">
                  {SUGGESTION_CHIPS.map((chip) => (
                    <button key={chip} className="ria-fab-chip" onClick={() => handleChipClick(chip)}>
                      {chip}
                    </button>
                  ))}
                </div>
              </>
            ) : (
              <>
                <div ref={riaTopSentinelRef} className="ria-fab-top-sentinel" />
                {activeDateLabel && (
                  <div className={`ria-fab-date-header${datePillVisible ? ' ria-fab-date-header-visible' : ''}`}>
                    <span>{activeDateLabel}</span>
                  </div>
                )}
                {loadingOlder && (
                  <div className="ria-fab-loading-older"><span className="ria-fab-spinner" /></div>
                )}
                {reachedHistoryStart && !loadingOlder && (
                  <div className="ria-fab-history-start">You've reached the beginning of your conversation history</div>
                )}
                {riaMessageGroups.map((group) => (
                  <div
                    key={group.dateKey}
                    className="ria-fab-day-group"
                    ref={(el) => {
                      if (el) riaDayGroupNodesRef.current.set(group.dateKey, el);
                      else riaDayGroupNodesRef.current.delete(group.dateKey);
                    }}
                  >
                    {group.items.map((msg, index) => (
                      <div key={index} className={`ria-fab-message${msg.role === 'user' ? ' user' : ''}`}>
                        {msg.role === 'ria' && (
                          <div className="ria-fab-avatar" style={{ backgroundImage: `url(${riaLogo})` }} role="img" aria-label="Ria" />
                        )}
                        <div className="ria-fab-bubble-col">
                          <span className="ria-fab-message-sender">
                            {msg.role === 'user' ? userDisplayName : 'Ria'}, {msg.time}
                          </span>
                          {msg.contextTag && <div className="ria-fab-context-tag">{msg.contextTag}</div>}
                          <div className="ria-fab-bubble">
                            {msg.role === 'ria' ? formatRiaMessage(msg.content) : msg.content}
                            {msg.role === 'ria' && msg.noteLinks?.length > 0 && (
                              <RiaNoteLinks notes={msg.noteLinks} />
                            )}
                            {msg.role === 'ria' && msg.cmaReportLink && (
                              <CmaReportLink link={msg.cmaReportLink} />
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ))}
              </>
            )}
            {loading && (
              <div className="ria-fab-message">
                <div className="ria-fab-avatar" style={{ backgroundImage: `url(${riaLogo})` }} role="img" aria-label="Ria" />
                <div className="ria-fab-bubble ria-fab-typing">
                  <span></span><span></span><span></span>
                </div>
              </div>
            )}
          </div>

          <div className="ria-fab-input-wrap">
            <AskRiaInputBar
              value={inputValue}
              onChange={setInputValue}
              onSend={handleSend}
              placeholder="Ask Ria..."
              loading={loading}
              showAddButton={false}
            />
          </div>
        </div>
      )}

      <button
        ref={btnRef}
        className={`ria-fab-btn${isOpen ? ' hidden' : ''}${isDragging ? ' dragging' : ''}`}
        style={{ left: `${position.x}px`, top: `${position.y}px` }}
        onMouseDown={handleBtnMouseDown}
        onTouchStart={handleBtnTouchStart}
        title="Ask Ria"
        aria-label="Ask Ria"
      >
        <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
          <path d="M12 2l2.09 6.26L21 9.27l-5 4.87 1.18 6.88L12 17.77l-5.18 3.25L8 14.14 3 9.27l6.91-1.01L12 2z" />
        </svg>
      </button>
    </>
  );
};

export default FloatingRiaWidget;
